import type {Ref} from 'vue'
import {nextTick, onBeforeUnmount, onMounted, ref} from 'vue'

export function useScrollState(
  containerRef: Ref<HTMLElement | null>,
  options: {direction?: 'horizontal' | 'vertical'; contentRef?: Ref<HTMLElement | null>} = {}
) {
  let resizeObserver: ResizeObserver | null = null
  const isScrollable = ref(false)
  const isAtStart = ref(true)
  const isAtEnd = ref(true)

  function updateScrollState() {
    const container = containerRef.value
    if (!container) {
      isScrollable.value = false
      isAtStart.value = true
      isAtEnd.value = true
      return
    }

    const horizontal = options.direction === 'horizontal'
    const position = horizontal ? container.scrollLeft : container.scrollTop
    const scrollSize = horizontal ? container.scrollWidth : container.scrollHeight
    const clientSize = horizontal ? container.clientWidth : container.clientHeight
    isScrollable.value = scrollSize - clientSize > 1
    isAtStart.value = !isScrollable.value || position <= 1
    isAtEnd.value = !isScrollable.value || scrollSize - clientSize - position <= 1
  }

  function observeTargets() {
    const container = containerRef.value
    if (!container || !resizeObserver) return

    resizeObserver.disconnect()
    resizeObserver.observe(container)
    if (options.contentRef?.value) resizeObserver.observe(options.contentRef.value)
  }

  onMounted(() => {
    nextTick(() => {
      const container = containerRef.value
      if (!container) return

      container.addEventListener('scroll', updateScrollState, {passive: true})
      resizeObserver = new ResizeObserver(updateScrollState)
      observeTargets()
      window.addEventListener('resize', updateScrollState)
      updateScrollState()
    })
  })

  onBeforeUnmount(() => {
    containerRef.value?.removeEventListener('scroll', updateScrollState)
    resizeObserver?.disconnect()
    window.removeEventListener('resize', updateScrollState)
  })

  return {isScrollable, isAtStart, isAtEnd, updateScrollState, observeTargets}
}
